import React, { FC, RefObject, useRef } from 'react';
import { useClientRequestForm } from '~/src/context/client-request-form/use-client-request-form.hook';
import { SectionContainerLeadPage } from '~/src/styles/shared-css/common-lead.styles';
import { ATTR_NAMES_LEAD } from '~/src/models/constants/attr-names-lead.constants';
import {
    initialCreateLeadDataContactFio,
    initialCreateLeadDataEmail,
    initialCreateLeadDataPhoneNumber,
    placeholders,
    titles,
} from '~/src/components/blocks/referral-products/organization-info-ref/components/person-contacts/person-contacts.constants';
import { FioField } from '~/src/components/common-components/fio-field/fio-field';
import checkFieldsSuccessFillForAnalytics from '~/src/helpers/analytics-ref/analytics-ref.helper';
import { onSendPersonContactsAnalytics } from './person-contacts.analytics';
import type { WebAnalyticsEventCategoryEnum } from '~/src/models/enums/web-analytics.enum';
import type { DataSuccessIconType } from '~/src/helpers/analytics-ref/analytics-ref.helper.types';
import * as Styled from './person-contacts.styles';

interface PersonContactsProps {
    inputErrorRef: RefObject<HTMLDivElement> | null;
    emailErrorRef: RefObject<HTMLDivElement> | null;
    selfEmployedFioErrorRef: RefObject<HTMLDivElement> | null;
    innErrorRef: RefObject<HTMLDivElement> | null;
    eventCategory: WebAnalyticsEventCategoryEnum;
    contactFioLabel?: string;
}

type UseRefType = DataSuccessIconType<string>;

export const PersonContacts: FC<PersonContactsProps> = ({
    inputErrorRef,
    emailErrorRef,
    innErrorRef,
    eventCategory,
    contactFioLabel,
    selfEmployedFioErrorRef,
}) => {
    const contactFioSuccessIconForAnalyticsRef = useRef<UseRefType>(initialCreateLeadDataContactFio);
    const phoneNumberSuccessIconForAnalyticsRef = useRef<UseRefType>(initialCreateLeadDataPhoneNumber);
    const phoneNumberErrorIconForAnalyticsRef = useRef<UseRefType>(initialCreateLeadDataPhoneNumber);
    const emailSuccessIconForAnalyticsRef = useRef<UseRefType>(initialCreateLeadDataEmail);
    const emailErrorIconForAnalyticsRef = useRef<UseRefType>(initialCreateLeadDataEmail);
    const { state, getTextValue, handleTextChange, clearField, getFieldError, clearFieldError } =
        useClientRequestForm();

    return (
        <SectionContainerLeadPage>
            <Styled.PersonContactsWrapper>
                {state.currentVariant === 'default' ? (
                    <FioField
                        name={ATTR_NAMES_LEAD.contactFio}
                        placeholder={placeholders.contactFio}
                        label={contactFioLabel || titles.contactFio}
                        validated
                        value={getTextValue(ATTR_NAMES_LEAD.contactFio)}
                        onValueChange={(e) => handleTextChange(ATTR_NAMES_LEAD.contactFio, e.target.value)}
                        onClear={() => clearField(ATTR_NAMES_LEAD.contactFio)}
                        analyticsref={checkFieldsSuccessFillForAnalytics.bind({
                            filedName: ATTR_NAMES_LEAD.contactFio,
                            dataSuccessRef: contactFioSuccessIconForAnalyticsRef,
                            sendAnalytics: () => onSendPersonContactsAnalytics('predstavitel', eventCategory),
                        })}
                    />
                ) : (
                    <Styled.PersonContactsContentContainer>
                        <Styled.FioTextbox
                            name={ATTR_NAMES_LEAD.contactFio}
                            label={titles.selfEmployedFio}
                            placeholder={placeholders.selfEmployedFio}
                            inputErrorRef={selfEmployedFioErrorRef}
                            value={getTextValue(ATTR_NAMES_LEAD.selfEmployedFio)}
                            onValueChange={(e) => handleTextChange(ATTR_NAMES_LEAD.selfEmployedFio, e.target.value)}
                            onClear={() => clearField(ATTR_NAMES_LEAD.selfEmployedFio)}
                        />
                        <Styled.InnTextbox
                            name={ATTR_NAMES_LEAD.inn}
                            placeholder={placeholders.selfEmployedInn}
                            label={titles.selfEmployedInn}
                            inputErrorRef={innErrorRef}
                            error={'Некорректное значение ИНН'}
                            value={getTextValue(ATTR_NAMES_LEAD.inn)}
                            onValueChange={(e) =>
                                handleTextChange(ATTR_NAMES_LEAD.inn, e.target.value.replace(/\D/g, ''))
                            }
                            onClear={() => clearField(ATTR_NAMES_LEAD.inn)}
                        />
                    </Styled.PersonContactsContentContainer>
                )}
                <Styled.PhoneEmailContainer>
                    <Styled.PhoneTextbox
                        name={ATTR_NAMES_LEAD.phoneNumber}
                        placeholder={placeholders.phone}
                        label={titles.phone}
                        required
                        maskChar={null}
                        inputErrorRef={inputErrorRef}
                        backendError={getFieldError(ATTR_NAMES_LEAD.phoneNumber)}
                        onBackendErrorClear={() => clearFieldError(ATTR_NAMES_LEAD.phoneNumber)}
                        analyticsref={checkFieldsSuccessFillForAnalytics.bind({
                            filedName: ATTR_NAMES_LEAD.phoneNumber,
                            dataSuccessRef: phoneNumberSuccessIconForAnalyticsRef,
                            sendAnalytics: () => onSendPersonContactsAnalytics('telefon', eventCategory),
                        })}
                        analyticsreferror={checkFieldsSuccessFillForAnalytics.bind({
                            filedName: ATTR_NAMES_LEAD.phoneNumber,
                            dataSuccessRef: phoneNumberErrorIconForAnalyticsRef,
                            sendAnalytics: () =>
                                onSendPersonContactsAnalytics('telefon', eventCategory, 'Неправильный формат телефона'),
                        })}
                    />
                    <Styled.EmailTextbox
                        name={ATTR_NAMES_LEAD.email}
                        placeholder={placeholders.email}
                        label={titles.email}
                        inputErrorRef={emailErrorRef}
                        analyticsref={checkFieldsSuccessFillForAnalytics.bind({
                            filedName: ATTR_NAMES_LEAD.email,
                            dataSuccessRef: emailSuccessIconForAnalyticsRef,
                            sendAnalytics: () => onSendPersonContactsAnalytics('email', eventCategory),
                        })}
                        analyticsreferror={checkFieldsSuccessFillForAnalytics.bind({
                            filedName: ATTR_NAMES_LEAD.email,
                            dataSuccessRef: emailErrorIconForAnalyticsRef,
                            sendAnalytics: () =>
                                onSendPersonContactsAnalytics('email', eventCategory, 'Неправильный формат почты'),
                        })}
                    />
                </Styled.PhoneEmailContainer>
            </Styled.PersonContactsWrapper>
        </SectionContainerLeadPage>
    );
};
